import { z } from 'zod';
import { ColumnSettingsSchema } from '../../shared/ColumnSelectPro/types';

export const JobRequestSchema = z.object({
    target_date: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .nullable()
        .catch(null),
    address_attributes: z
        .object({
            city: z.string().nullable().catch(null),
            house: z.string().nullable().catch(null),
            apartment: z.string().nullable().catch(null),
            pcode: z.string().nullable().catch(null),
            street: z.string().nullable().catch(null),
            country: z.string().nullable().catch(null),
            quarter: z.string().nullable().catch(null),
            province: z.string().nullable().catch(null),
            community: z.string().nullable().catch(null),
        })
        .nullable()
        .catch(null),
    special_attributes: z
        .object({
            type: z
                .union([z.string(), z.array(z.string())])
                .nullable()
                .catch(null),
            phone: z.string().nullable().catch(null),
            parcels: z.coerce.number().nullable().catch(null),
            total_weight: z.coerce.number().nullable().catch(null),
            deliverydelay: z.coerce.string().nullable().catch(null),
            fill_percentage: z.coerce.number().nullable().catch(null),
            end_of_time_window: z.string().nullable().catch(null),
            external_id: z.coerce.string().nullable().catch(null),
            latest_delivery_date: z.string().nullable().catch(null),
            start_of_time_window: z.string().nullable().catch(null),
            delivery_or_collection: z
                .string()
                .nullable()
                .catch(null)
                .describe('delivery / collection'),
            earliest_delivery_date: z.string().nullable().catch(null),
            orderCreatedDate: z.string().nullable().catch(null),
            vehicle_email_to_assign: z.string().nullable().catch(null),
            vehicle_name_to_assign: z.string().nullable().catch(null),
            scannable: z.boolean().nullable().catch(null),
            label: z.string().nullable().catch(null),
        })
        .nullable()
        .catch(null)
    ,
    client_attributes: z.record(z.any()).catch({}),
    geom: z
        .object({
            type: z.literal('Point'),
            coordinates: z.array(z.number()).length(2),
        })
        .nullable()
        .catch(null),
    coords_set_manually: z.boolean().catch(false),
    webuser_id: z.number().nullable().catch(null),
    collection_id: z.number().nullable().catch(null),
    job_type_id: z.number().catch(1), // SELECT * FROM public.job_type; 1=single 2=schedule
    import_id: z.number().nullable().catch(null),
    address_id: z.coerce.number().nullable().catch(null),
    coords_accepted: z.boolean().catch(false),
    rejected: z.boolean().catch(false).describe('If the job is rejected by the driver or operator'),
    vehicle_id: z.number().nullable().catch(null).describe('id of vehicle preferred by the client to assign'),
    assigned_vehicle_id: z.number().nullable().catch(null).describe('id of the vehicle finally assigned to the job'),
    order_created_date: z.string().nullable().catch(null),
});
export type JobRequestType = z.infer<typeof JobRequestSchema>;

export const JobResponseSchema = JobRequestSchema.merge(
    z.object({
        /* added by the server when query for assignment_jobs */
        parcels: z.coerce.number().catch(0),
        doc: z.string().catch('').describe('delivery / '),
        target_date: z.string().nullable().catch(null),
        geom: z.any(),
        /* --- */

        process_status: z.number().catch(0),
        process_stage: z.number().catch(0),
        operate_status: z.number().catch(0),
        end_date: z
            .string()
            .regex(/^\d{4}-\d{2}-\d{2}$/)
            .nullable()
            .catch(null),
        updated: z.string().nullable().catch(null).describe('2023-11-30 01:28:50'),
        created: z.string().nullable().catch(null).describe('2023-11-30 01:28:50'),
        start_date: z
            .string()
            .regex(/^\d{4}-\d{2}-\d{2}$/)
            .nullable()
            .catch(null),
        schedule_id: z.number().nullable().catch(null),
        newly_added: z.boolean().catch(false),
        completed: z
            .string()
            .regex(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/)
            .nullable()
            .catch(null),
        ad_hoc: z.boolean().catch(false),
        id: z.number(),
        rejected_assignments: z
            .array(z.number())
            .catch([])
            .describe('Array of rejected assignment ids'),
        computed_address: z.object({
            city: z.string().nullable().catch(null),
            house: z.string().nullable().catch(null),
            pcode: z.string().nullable().catch(null),
            street: z.string().nullable().catch(null),
            district: z.string().nullable().catch(null),
            province: z.string().nullable().catch(null),
            community: z.string().nullable().catch(null),
            house_as_number: z.number().nullable().catch(null),
            street_for_sorting: z.string().nullable().catch(null),
        }).optional().describe('{"city": "Warszawa", "house": "43/47", "pcode": "02-288", "street": "Kolumba Krzysztofa", "district": "Warszawa", "province": "mazowieckie", "community": "Warszawa", "house_as_number": 43, "street_for_sorting": "Kolumba Krzysztofa"}')
    })
);
export type JobResponseType = z.infer<typeof JobResponseSchema>;

export const JobServiceAttributesSchema = z.object({
    load: z.object({
        time: z.number(),
        scanned: z.boolean()
    }).optional(),
    delivery: z.number().optional(),
    rejection: z.number().optional(),
    photo: z.array(z.string()).optional(),
    note: z.string().optional(),
    tags: z.array(z.string()).optional(),
    extra: z.record(z.string(), z.any()).optional()
});
export type JobServiceAttributesType = z.infer<typeof JobServiceAttributesSchema>;

export const JobRealisationSchema = z.object({
    service_attributes: JobServiceAttributesSchema.optional(),
});

export const JobWithRealisationResponseSchema = JobResponseSchema.merge(
    z.object({
        realisation: JobRealisationSchema.nullable().optional(),
    })
);
export type JobWithRealisationResponseType = z.infer<typeof JobWithRealisationResponseSchema>;

export const JobWithColumnAssignmentsResponseSchema = JobResponseSchema.merge(
    z.object({
        import: z.object({columns_assignment: ColumnSettingsSchema}),
    })
);
export type JobWithColumnAssignmentsResponseType = z.infer<typeof JobWithColumnAssignmentsResponseSchema>;

export const JobWithRealisationAndRecentRejectionResponseSchema = JobWithRealisationResponseSchema.merge(
    z.object({
        rejection: JobRealisationSchema.nullable().optional(),
    })
);
export type JobWithRealisationAndRecentRejectionResponseType = z.infer<typeof JobWithRealisationAndRecentRejectionResponseSchema>;

export const JobCountSchema = z.object({
    count: z.coerce.number().catch(0),
});
export type JobCountType = z.infer<typeof JobCountSchema>;
